import React from "react"
import { Col, Container, Row } from "reactstrap"
import styled from "styled-components"
import vars from "styles/variables"
import PageTemplate from "templates/PageTemplate"
import useLocoScroll from "hooks/useLocoScroll"
import useImagePreloader from "hooks/useImagePreloader"
import Seo from "components/common/Seo"
import useEnvironment from "hooks/useEnvironment"
import WhyChooseUs from "components/core/WhyChooseUs"
import BackToTop from "components/common/BackToTop"
import ImgEnv1 from "assets/images/env1.png"
import ImgEnv2 from "assets/images/env2.jpg"
import ImgEnv3 from "assets/images/env3.png"
import ImgEnv4 from "assets/images/env4.png"

const preloadSrcList = [ImgEnv1, ImgEnv2, ImgEnv3, ImgEnv4]

export default function EnvironmentPage() {
  const { imagesPreloaded } = useImagePreloader(preloadSrcList)
  const { locoScrollTop } = useLocoScroll(imagesPreloaded)
  const { environmentRef } = useEnvironment(imagesPreloaded)

  if (!imagesPreloaded) return null

  return (
    <StyledEnvironmentPage ref={environmentRef}>
      <Seo title="Environment" />
      <BackToTop onClick={locoScrollTop} />
      <PageTemplate title="Environment" hideTitle>
        <Container>
          <Row className="mb-5 align-items-center" data-scroll-section>
            <Col xs="12">
              <StyledTitle className="enviro-title">
                <div
                  data-scroll
                  data-scroll-speed="2"
                  data-scroll-position="top"
                  data-scroll-direction="horizontal"
                >
                  <span>N</span>
                </div>
                <div
                  data-scroll
                  data-scroll-speed="1"
                  data-scroll-position="top"
                  data-scroll-direction="horizontal"
                >
                  <span>E</span>
                </div>
                <div
                  data-scroll
                  data-scroll-speed="0.5"
                  data-scroll-position="top"
                  data-scroll-direction="horizontal"
                >
                  <span>T</span>
                </div>

                <div
                  data-scroll
                  data-scroll-speed="-1"
                  data-scroll-position="top"
                  data-scroll-direction="horizontal"
                >
                  <span>ZERO</span>
                </div>
              </StyledTitle>
              <div className="enviro-quote">
                <StyledQuote data-scroll>
                  &#8220;We can&#8217;t save the world by playing by the rules,
                  because the rules have to be changed. Everything needs to
                  change and it has to start today&#8221;
                </StyledQuote>
              </div>
            </Col>
          </Row>

          <Row className="mb-5" data-scroll-section>
            <Col xs="12">
              <p>
                At I/O Atelier, we believe that sustainability begins with
                thoughtful design. Achieving Net Zero isn’t just about reducing
                emissions; it’s about creating spaces that inspire and endure
                while minimizing their environmental impact. Our pledge is to
                integrate sustainability into every project, making choices
                today that matter for generations to come.
              </p>
            </Col>
          </Row>

          <Row data-scroll-section>
            <Col xs="12" md="6" lg="4">
              <h2>Our Commitment to Carbon Neutrality</h2>
            </Col>
          </Row>

          <Row className="mb-5 align-items-start" data-scroll-section>
            <Col xs="12" lg="6">
              <img
                src={ImgEnv1}
                alt="Net Zero Graph"
                className="img-fluid w-100 mb-4 mb-md-0"
              />
            </Col>
            <Col xs="12" lg="6">
              <p>
                We are committed to:
                <br />
                <br />
                <strong>Reducing embodied carbon</strong> by carefully selecting
                materials and processes that minimize environmental impact.
                <br />
                <br />
                <strong>Promoting reuse</strong> by breathing new life into
                existing spaces and materials, reducing waste wherever possible.
                <br />
                <br />
                <strong>Collaborating with clients</strong> by providing clear
                options and actionable strategies to achieve low, neutral, or
                negative embodied carbon outcomes.
              </p>
            </Col>
          </Row>

          <Row data-scroll-section>
            <Col xs="12">
              <p className="mb-5">
                <StyledList>
                  Key Goals:
                  <br />
                  <br />
                  <li>
                    To integrate sustainable principles into every stage of the
                    design process.
                  </li>
                  <li>
                    To empower clients to make informed decisions by presenting
                    practical and impactful solutions.
                  </li>
                  <li>
                    To push creative boundaries while prioritizing environmental
                    responsibility.
                  </li>
                </StyledList>
              </p>
            </Col>
          </Row>

          <Row className="w-100 d-flex justify-content-end" data-scroll-section>
            <Col xs="12">
              <div
                className="mb-5"
                style={{
                  width: "100%",
                  textAlign: "left",
                  textAlignLast: "left",
                  direction: "ltr",
                  paddingRight: "5%",
                }}
              >
                <h2 style={{ fontWeight: "bold", textAlign: "right" }}>
                  What we do?
                </h2>
                <p
                  style={{
                    fontSize: "2rem",
                    fontWeight: "200",
                    lineHeight: "1.6",
                  }}
                >
                  Our mission is to reduce the carbon footprint of everything we
                  create by embedding sustainability into our design process.
                </p>
              </div>
            </Col>
          </Row>

          <Row className="mb-5 align-items-top" data-scroll-section>
            <Col>
              <h3 style={{ fontWeight: "bold", textAlign: "left" }}>
                Material Selection
              </h3>
              <ul>
                <li>
                  Prioritizing recycled, reusable, and low-carbon materials.
                </li>
                <li>
                  Avoiding the use of virgin materials whenever alternatives are
                  available.
                </li>
              </ul>
            </Col>
            <Col>
              <h3 style={{ fontWeight: "bold", textAlign: "left" }}>
                Circular Design Principles
              </h3>
              <ul>
                <li>
                  Creating adaptable spaces that can evolve with changing needs.
                </li>
                <li>
                  Designing for disassembly, ensuring materials can be reused or
                  repurposed.
                </li>
              </ul>
            </Col>
            <Col>
              <h3 style={{ fontWeight: "bold", textAlign: "left" }}>
                Pragmatic Solutions
              </h3>
              <ul>
                <li>
                  Offering practical and achievable recommendations that align
                  with client goals and budgets.
                </li>
                <li>
                  Ensuring sustainability doesn’t compromise creativity or
                  functionality.
                </li>
              </ul>
            </Col>
          </Row>

          <Row className="mb-5 align-items-start" data-scroll-section>
            <Col xs="12">
              <img
                src={ImgEnv2}
                alt="Net Zero Graph"
                className="img-fluid w-100"
                style={{ borderRadius: "20px" }}
              />
              <p
                style={{
                  fontSize: "0.7rem",
                  fontWeight: "300",
                  marginTop: "10px",
                  textAlign: "right",
                  paddingRight: "0%",
                }}
              >
                Private Residence in Greece
              </p>
            </Col>
          </Row>

          <Row className="w-100 d-flex justify-content-end" data-scroll-section>
            <CircularDesignCol xs="12">
              <CircularDesignWrapper className="mb-5">
                <h2 style={{ fontWeight: "bold", textAlign: "right" }}>
                  Circular Design Strategies: How We Make an Impact
                </h2>
                <p
                  style={{
                    fontSize: "2rem",
                    fontWeight: "200",
                    lineHeight: "1.6",
                  }}
                >
                  We focus on strategies that ensure materials and resources
                  remain in use for as long as possible, minimizing waste and
                  environmental harm.
                </p>
              </CircularDesignWrapper>
            </CircularDesignCol>
          </Row>

          <Row className="mb-5 align-items-center" data-scroll-section>
            <Col xs="12" lg="12">
              <GridContainer>
                <LeftImage>
                  <Image src={ImgEnv3} alt="Top aligned" />
                </LeftImage>

                <TextBlock>
                  <h3>Recycle</h3>
                  <p>
                    Reuse materials wherever possible to extend their lifecycle
                    and reduce the need for new resources.
                  </p>

                  <h3>Gut and Renew</h3>
                  <p>
                    Reuse materials wherever possible to extend their lifecycle
                    and reduce the need for new resources.
                  </p>
                </TextBlock>

                <TextBlock>
                  <h3>Modularity</h3>
                  <p>
                    Design flexible interiors that adapt to future needs,
                    reducing the need for frequent renovations.
                  </p>

                  <h3>Experimentation</h3>
                  <p>
                    Continuously explore new sustainable materials and
                    techniques to drive innovation.
                  </p>
                </TextBlock>

                <RightImage>
                  <Image src={ImgEnv4} alt="Bottom aligned" />
                  <Caption align="right">
                    Biophilic design for an office space in Dubai
                  </Caption>
                </RightImage>
              </GridContainer>
            </Col>
          </Row>
        </Container>
        <WhyChooseUs />
      </PageTemplate>
    </StyledEnvironmentPage>
  )
}

const StyledList = styled.ul`
  list-style-type: disc; /* This is the default bullet style */
  padding-left: 20px;

  li {
    margin-bottom: 8px;
  }
`

const StyledEnvironmentPage = styled.div`
  h3 {
    margin: 0 0 16px 0;
    font-size: 24px;
    font-weight: 600;

    @media (max-width: ${vars.screen_sm_max}) {
      font-size: 18px;
    }
  }

  ul {
    padding-left: 16px;

    li {
      margin-bottom: 8px;
    }
  }

  .fade-in-slide {
    opacity: 0;
    transform: translate(50px, 0px);
  }

  .co2-2 {
    max-width: 100px;
    margin: 0 auto;
    display: block;
  }

  .enviro-quote,
  .carbon-title {
    opacity: 0;
    transform: translate(0, 50px);
  }

  .enviro-banner {
    opacity: 0;
    transform: translate(50px, 50px);
  }
`

const StyledTitle = styled.h2`
  font-size: 56px;
  line-height: 1;
  font-weight: 600;
  letter-spacing: 5px;
  margin-bottom: 30px;

  span {
    display: block;
    opacity: 0;
    transform: rotate(-45deg);
    transform: translate(40px, 40px);
  }
`

const StyledQuote = styled.blockquote`
  font-family: ${vars.secondaryFont};
  font-size: 46px;
  margin: 0 0 20px 0;

  @media (max-width: ${vars.screen_md_max}) {
    font-size: 32px;
  }
`

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: 1.5fr 1fr 1fr;
  grid-template-rows: auto auto auto auto auto;
  padding: 20px;

  @media (max-width: ${vars.screen_md_max}) {
    display: flex;
    flex-direction: column;
    padding: 0;
  }
`

const LeftImage = styled.div`
  grid-column: 1 / 2;
  grid-row: 1 / 6;
  display: flex;
  flex-direction: column;

  @media (max-width: ${vars.screen_md_max}) {
    grid-column: auto;
    grid-row: auto;
  }
`

const RightImage = styled.div`
  grid-column: 3 / 4;
  grid-row: 4 / 6;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: flex-end;

  @media (max-width: ${vars.screen_md_max}) {
    align-items: flex-start;
    text-align: left;
    grid-column: auto;
    grid-row: auto;
  }
`

const TextBlock = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  text-align: left;
  padding: 20px;

  @media (max-width: ${vars.screen_md_max}) {
    grid-column: auto;
    grid-row: auto;
    padding-left: 0;
    padding-right: 0;
  }

  &:nth-child(2) {
    grid-column: 2 / 4;
    grid-row: 1 / 4;
    padding-bottom: 0;

    @media (max-width: ${vars.screen_md_max}) {
      grid-column: auto;
      grid-row: auto;
    }
  }

  &:nth-child(3) {
    padding-top: 0;

    @media (max-width: ${vars.screen_md_max}) {
      grid-column: auto;
      grid-row: auto;
    }
  }
`

const Caption = styled.p`
  font-size: 0.7rem;
  font-weight: 300;
  margin-top: 10px;
  text-align: ${({ align }) => align || "left"};
  width: 100%;
`

const Image = styled.img`
  width: 100%;
  height: auto;
  border-radius: 10px;
`

const CircularDesignCol = styled(Col)`
  @media (max-width: ${vars.screen_xs_max}) {
    display: flex;
    justify-content: flex-end;
  }
`

const CircularDesignWrapper = styled.div`
  width: 80%;
  max-width: 900px;
  text-align: right;
  text-align-last: right;
  direction: ltr;

  @media (max-width: ${vars.screen_xs_max}) {
    width: 100%;
  }
`
